import React from "react";
import { Logo } from "../components/Logo";

interface LandingProps {
  onNavigate: (page: string) => void;
}

export const Landing: React.FC<LandingProps> = ({ onNavigate }) => {
  return (
    <div className="bg-[#f8fafc] min-h-screen flex flex-col font-['Inter'] selection:bg-blue-100 selection:text-blue-900">
      {/* Cabeçalho */}
      <nav className="max-w-7xl mx-auto w-full px-4 sm:px-6 py-6 sm:py-10 flex justify-between items-center relative z-20 min-w-0">
        <div
          className="flex items-center gap-4 sm:gap-6 cursor-pointer group min-w-0"
          onClick={() => onNavigate("landing")}
        >
          <Logo
            size="md"
            className="group-hover:scale-105 transition-transform duration-500 shrink-0"
          />

          <div className="flex flex-col min-w-0">
            <span className="text-2xl sm:text-4xl font-black tracking-tighter text-[#1e293b] leading-none uppercase truncate">
              UroInsights
            </span>
            <span className="text-[10px] sm:text-sm font-black text-blue-600 uppercase tracking-[0.5em] mt-1.5 sm:mt-2">
              Academy
            </span>
          </div>
        </div>

        <div className="flex gap-3 sm:gap-6 shrink-0">
          <button
            onClick={() => onNavigate("login")}
            className="hidden md:block px-6 py-3 font-black text-[#64748b] hover:text-[#1e293b] transition-colors uppercase text-sm tracking-widest"
          >
            Login
          </button>

          <button
            onClick={() => onNavigate("signup")}
            className="px-5 sm:px-10 py-3 sm:py-4 bg-[#1d4ed8] text-white rounded-2xl font-black shadow-2xl shadow-blue-200 hover:bg-[#1e40af] transition-all transform hover:scale-105 active:scale-95 text-sm sm:text-base"
          >
            Cadastrar
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative flex flex-col items-center justify-center text-center px-4 sm:px-6 pt-8 sm:pt-12 pb-16 sm:pb-24 overflow-hidden">
        {/* Floating Icons */}
        <div className="absolute top-20 left-10 animate-bounce hidden lg:block opacity-40">
          <div className="w-16 h-16 bg-white rounded-2xl shadow-xl flex items-center justify-center text-3xl">
            🩺
          </div>
        </div>
        <div className="absolute bottom-40 right-10 animate-bounce hidden lg:block opacity-40 [animation-delay:500ms]">
          <div className="w-16 h-16 bg-white rounded-2xl shadow-xl flex items-center justify-center text-3xl">
            🎮
          </div>
        </div>
        <div className="absolute top-40 right-20 animate-pulse hidden lg:block opacity-40">
          <div className="w-12 h-12 bg-amber-400 rounded-full shadow-lg flex items-center justify-center text-white font-black">
            XP
          </div>
        </div>
        <div className="absolute bottom-20 left-20 animate-pulse hidden lg:block opacity-40 [animation-delay:700ms]">
          <div className="w-14 h-14 bg-white rounded-2xl shadow-xl flex items-center justify-center text-3xl">
            🏆
          </div>
        </div>

        <div className="max-w-5xl mx-auto flex flex-col items-center relative z-10">
          <h1 className="text-4xl sm:text-6xl md:text-8xl font-black text-[#1e293b] leading-[0.9] md:leading-[0.85] tracking-tighter mb-6 sm:mb-10">
            Aprenda <span className="text-[#2563eb]">Urologia</span> <br />
            com a UroInsights
          </h1>

          <p className="text-base sm:text-xl md:text-2xl text-[#64748b] max-w-4xl mx-auto mb-10 sm:mb-16 leading-relaxed font-bold">
            <span className="text-[#1e293b] font-black">UroInsights:</span> A
            Plataforma completa para estudar Urologia com questões, flashcards,
            simulados e sistema de progressão gamificado.
          </p>

          <div className="flex flex-col sm:flex-row justify-center gap-4 sm:gap-6 mb-12 sm:mb-20 w-full max-w-xl">
            <button
              onClick={() => onNavigate("signup")}
              className="flex-1 px-8 sm:px-12 py-5 sm:py-7 bg-[#1d4ed8] text-white rounded-[2.5rem] text-lg sm:text-2xl font-black shadow-2xl shadow-blue-200 hover:bg-[#1e40af] transition-all transform active:scale-95"
            >
              Começar Agora
            </button>

            <button
              onClick={() => onNavigate("login")}
              className="flex-1 px-8 sm:px-12 py-5 sm:py-7 bg-white text-[#1e293b] border-2 border-slate-100 rounded-[2.5rem] text-lg sm:text-2xl font-black hover:bg-slate-50 transition-all shadow-lg active:scale-95"
            >
              Fazer Login
            </button>
          </div>
        </div>

        {/* Background glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[150%] h-[150%] bg-blue-50 rounded-full blur-[120px] -z-10 opacity-60" />
      </section>

      {/* SEÇÃO: POR QUE FUNCIONA */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-20">
            <p className="text-blue-600 font-black text-xs uppercase tracking-[0.4em] mb-4">
              Engenharia de Estudo
            </p>
            <h2 className="text-3xl sm:text-5xl font-black text-slate-900 mb-4 sm:mb-6 tracking-tight uppercase">
              Por que a plataforma funciona?
            </h2>
            <p className="text-slate-500 font-bold text-base sm:text-xl max-w-2xl mx-auto">
              Metodologias aplicadas para maximizar sua retenção e performance em
              provas de título.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            <BenefitCard
              icon="🎯"
              title="Simulados inteligentes"
              description="Treine por tema e acompanhe erros em tempo real para revisar exatamente o que mais cai."
              bgColor="bg-blue-50"
            />
            <BenefitCard
              icon="🧠"
              title="Flashcards com revisão"
              description="Memorize com repetição espaçada (SM2) para reter o conteúdo por muito mais tempo."
              bgColor="bg-amber-50"
            />
            <BenefitCard
              icon="🔥"
              title="Gamificação com XP"
              description="Estude com metas, níveis e streaks de atividade para manter a consistência diária."
              bgColor="bg-emerald-50"
            />
            <BenefitCard
              icon="📉"
              title="Foco no que você erra"
              description="O 'mapa de fraquezas' inteligente guia suas próximas sessões para fechar lacunas."
              bgColor="bg-rose-50"
            />
            <BenefitCard
              icon="💡"
              title="Conteúdo comentado"
              description="Explicações objetivas e diretas para aprender o raciocínio clínico, não apenas decorar."
              bgColor="bg-indigo-50"
            />
            <BenefitCard
              icon="⏱️"
              title="Estudo rápido no plantão"
              description="Sessões curtas de 10-15 min otimizadas para mobile para caber em qualquer momento da rotina."
              bgColor="bg-slate-50"
            />
          </div>
        </div>
      </section>

      {/* SEÇÃO: EVOLUÇÃO */}
      <section className="py-16 sm:py-24 bg-slate-50 border-y border-slate-100 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col lg:flex-row items-center gap-10 sm:gap-20">
            <div className="lg:w-1/2">
              <span className="text-blue-600 font-black text-xs uppercase tracking-[0.3em] mb-4 block">
                Dashboard de Performance
              </span>
              <h2 className="text-4xl sm:text-6xl font-black text-slate-900 mb-6 sm:mb-8 tracking-tighter leading-tight">
                Seu progresso é <br />
                <span className="text-blue-600">quantificável.</span>
              </h2>
              <p className="text-slate-500 text-base sm:text-xl font-bold leading-relaxed mb-8 sm:mb-10">
                Acompanhe sua evolução em percentil de cada Tema da Uro e veja
                sua retenção de longo prazo aumentar com nossa metodologia.
              </p>

              <div className="space-y-6">
                <AnalyticsPoint
                  title="Evolução por Percentil"
                  desc="Acompanhe sua evolução em percentil de cada Tema da Uro."
                />
                <AnalyticsPoint
                  title="Mapa de Calor"
                  desc="Visualização instantânea de forças e fraquezas por área urológica."
                />
                <AnalyticsPoint
                  title="Curva de Esquecimento"
                  desc="Métricas que provam como a revisão espaçada combate a perda de memória."
                />
              </div>
            </div>

            <div className="lg:w-1/2 relative w-full">
              <div className="bg-white p-6 sm:p-10 rounded-[2.5rem] sm:rounded-[3rem] shadow-2xl border border-slate-200 relative z-10 transform lg:rotate-2">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-8">
                  <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest">
                    Sua Performance Global
                  </h3>
                  <div className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-lg font-black text-[10px] uppercase w-fit">
                    Top 8% Nacional
                  </div>
                </div>

                <div className="space-y-6">
                  <PerformanceBar
                    label="Uro-Oncologia"
                    percent={88}
                    color="bg-blue-600"
                  />
                  <PerformanceBar
                    label="Uropediatria"
                    percent={65}
                    color="bg-indigo-500"
                  />
                  <PerformanceBar
                    label="Litíase"
                    percent={92}
                    color="bg-emerald-500"
                  />
                  <PerformanceBar
                    label="Andrologia"
                    percent={45}
                    color="bg-amber-500"
                  />
                </div>

                <div className="mt-10 sm:mt-12 pt-8 border-t border-slate-100 relative">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6">
                    Curva de Retenção (Metodologia Ebbinghaus)
                  </p>
                  <div className="h-28 sm:h-32 flex items-end gap-1 relative">
                    <svg
                      viewBox="0 0 400 100"
                      className="absolute bottom-0 left-0 w-full h-full overflow-visible"
                    >
                      <path
                        d="M0,20 C50,80 150,90 400,95"
                        fill="none"
                        stroke="#e2e8f0"
                        strokeWidth="2"
                        strokeDasharray="4"
                      />
                      <path
                        d="M0,0 
                           C20,40 40,60 60,70 L60,0 
                           C80,30 100,50 120,55 L120,5 
                           C150,25 200,40 250,45 L250,15 
                           C300,30 350,35 400,38"
                        fill="none"
                        stroke="#3b82f6"
                        strokeWidth="3"
                        strokeLinecap="round"
                      />
                      <circle cx="0" cy="0" r="3" fill="#ef4444" />
                      <circle cx="60" cy="0" r="3" fill="#ef4444" />
                      <circle cx="120" cy="5" r="3" fill="#ef4444" />
                      <circle cx="250" cy="15" r="3" fill="#ef4444" />
                    </svg>
                  </div>

                  <div className="flex justify-between mt-4 text-[8px] font-black text-slate-400 uppercase tracking-widest">
                    <span>Estudo</span>
                    <span>Rev 1</span>
                    <span>Rev 2</span>
                    <span>Rev 3</span>
                    <span>Domínio</span>
                  </div>
                </div>
              </div>

              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-blue-100/40 rounded-full blur-3xl -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* NOVO SETOR: PREPARE-SE PARA */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="bg-gradient-to-br from-slate-900 via-blue-900 to-[#1d4ed8] rounded-[2.5rem] sm:rounded-[4rem] p-8 sm:p-12 md:p-20 text-white shadow-2xl relative overflow-hidden flex flex-col lg:flex-row items-center gap-10 sm:gap-16">
            <div className="lg:w-1/2 relative z-10">
              <h2 className="text-3xl sm:text-5xl md:text-6xl font-black mb-8 leading-none tracking-tighter">
                Prepare-se para{" "}
                <span className="text-amber-400 underline decoration-white/20">
                  vencer
                </span>
                .
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
                <PrepItem title="Atualizações" desc="Guidelines 2024/25 sempre em dia." />
                <PrepItem title="Provas de Título" desc="Preparatório focado no TiSBU." />
                <PrepItem title="Residência Médica" desc="Domine a base da especialidade." />
                <PrepItem title="Provas de Fellow" desc="Diferencial em subespecialidades." />
              </div>

              <p className="mt-10 sm:mt-12 text-base sm:text-xl font-bold text-blue-100 border-l-4 border-amber-400 pl-6">
                Otimize seu aprendizado no R1/R2/R3 de Urologia com uma jornada
                de elite.
              </p>
            </div>

            <div className="lg:w-1/2 relative w-full">
              <div className="relative z-10 rounded-[2.5rem] sm:rounded-[3rem] overflow-hidden shadow-2xl border-4 border-white/10 group">
                <img
                  src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?q=80&w=1200&auto=format&fit=crop"
                  alt="Urologista em treinamento"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-[320px] sm:h-[400px] object-cover group-hover:scale-110 transition-transform duration-1000"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/80 to-transparent" />
                <div className="absolute bottom-6 sm:bottom-10 left-6 sm:left-10">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="px-3 py-1 bg-amber-400 text-blue-900 rounded-full text-[10px] font-black uppercase tracking-tighter">
                      Você UroInsighter
                    </span>
                    <span className="text-white text-xs font-bold flex items-center gap-1">
                      🏆 Elite Acadêmica
                    </span>
                  </div>
                  <h4 className="text-xl sm:text-2xl font-black">
                    Excelência Cirúrgica e Clínica
                  </h4>
                </div>
              </div>

              <div className="absolute -top-10 -right-10 w-24 h-24 bg-white/10 backdrop-blur-md rounded-3xl rotate-12 flex items-center justify-center text-4xl shadow-xl border border-white/20 hidden sm:flex">
                🔬
              </div>

              <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl -mr-32 -mt-32" />
            </div>
          </div>
        </div>
      </section>

      {/* SEÇÃO: REFERÊNCIAS */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-xs font-black text-slate-400 uppercase tracking-[0.5em] mb-12 sm:mb-16">
            Baseado nas Maiores Referências da Urologia Mundial
          </h2>
          <div className="flex flex-wrap justify-center gap-4 sm:gap-6 md:gap-8 lg:gap-10">
            <ReferenceTile icon="📖" name="UpToDate" color="text-emerald-500" bgColor="bg-emerald-50" />
            <ReferenceTile icon="📘" name="Campbell-Walsh 13th" color="text-blue-500" bgColor="bg-blue-50" />
            <ReferenceTile icon="📄" name="AUA Guidelines" color="text-red-500" bgColor="bg-red-50" />
            <ReferenceTile icon="📄" name="EAU Guidelines" color="text-blue-700" bgColor="bg-indigo-50" />
            <ReferenceTile icon="📄" name="SBU" color="text-emerald-600" bgColor="bg-emerald-50" />
          </div>
        </div>
      </section>

      {/* Seção de Planos */}
      <section
        id="planos"
        className="py-20 sm:py-32 bg-slate-50 border-t border-slate-100"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-5xl font-black text-slate-900 mb-4 sm:mb-6 tracking-tight uppercase">
            Planos Academy Premium
          </h2>
          <p className="text-slate-500 font-bold text-base sm:text-xl mb-10 sm:mb-20">
            Acelere seu conhecimento com acesso total.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-10">
            <div className="p-8 sm:p-12 bg-white rounded-[2.5rem] sm:rounded-[3.5rem] border border-slate-100 flex flex-col text-left hover:shadow-xl transition-all">
              <h3 className="text-2xl sm:text-3xl font-black mb-4">Gratuito</h3>
              <div className="text-4xl sm:text-5xl font-black text-slate-400 mb-8 sm:mb-10">
                R$ 0
              </div>
              <ul className="space-y-4 sm:space-y-5 mb-10 sm:mb-12 text-slate-500 font-bold text-base sm:text-lg flex-1">
                <li className="flex items-center gap-3">✓ 10% do conteúdo</li>
                <li className="flex items-center gap-3">✓ Questões básicas</li>
                <li className="flex items-center gap-3">
                  ✓ Flashcards essenciais
                </li>
                <li className="flex items-center gap-3 opacity-30">
                  ✕ Simulados Premium
                </li>
              </ul>
              <button
                onClick={() => onNavigate("signup")}
                className="w-full py-4 sm:py-5 bg-slate-100 text-slate-600 rounded-3xl font-black text-base sm:text-lg hover:bg-slate-200 transition-all"
              >
                Começar Grátis
              </button>
            </div>

            <div className="p-8 sm:p-12 bg-blue-900 rounded-[2.5rem] sm:rounded-[3.5rem] border-4 border-amber-400 flex flex-col text-left text-white shadow-2xl md:scale-105 relative">
              <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-amber-400 text-blue-900 px-6 sm:px-8 py-2 rounded-full font-black text-xs sm:text-sm uppercase tracking-widest shadow-lg">
                MAIS POPULAR
              </div>
              <h3 className="text-2xl sm:text-3xl font-black mb-4">
                Premium Anual
              </h3>
              <div className="text-5xl sm:text-6xl font-black mb-3 sm:mb-4 tracking-tighter">
                R$ 750
              </div>
              <div className="text-base sm:text-lg font-bold text-blue-200 mb-8 sm:mb-10">
                Equivale a R$ 62,50/mês
              </div>
              <ul className="space-y-4 sm:space-y-5 mb-10 sm:mb-12 text-blue-50 font-bold text-base sm:text-lg flex-1">
                <li className="flex items-center gap-3">
                  ✓ 100% do conteúdo liberado
                </li>
                <li className="flex items-center gap-3">
                  ✓ +1.000 questões comentadas
                </li>
                <li className="flex items-center gap-3">
                  ✓ Decks ilimitados (SM2)
                </li>
                <li className="flex items-center gap-3">
                  ✓ Suporte via WhatsApp
                </li>
              </ul>
              <button
                onClick={() => onNavigate("signup")}
                className="w-full py-5 sm:py-6 bg-amber-400 text-blue-900 rounded-3xl font-black text-lg sm:text-xl hover:bg-amber-300 shadow-xl transform active:scale-95 transition-all"
              >
                Assinar Agora 💎
              </button>
            </div>

            {/* Premium 24 Meses */}
            <div className="p-12 bg-white rounded-[3.5rem] border border-slate-100 flex flex-col text-left hover:shadow-xl transition-all shadow-2xl">
              <h3 className="text-3xl font-black mb-4">Premium 24 Meses</h3>
              <div className="text-5xl font-black text-slate-800 mb-10">
                R$ 1248
              </div>

              <ul className="space-y-5 mb-12 text-slate-500 font-bold text-lg flex-1">
                <li className="flex items-center gap-3">
                  ✓ 100% do conteúdo liberado
                </li>
                <li className="flex items-center gap-3">
                  ✓ Decks ilimitados (SM2)
                </li>
                <li className="flex items-center gap-3">
                  ✓ Gamificação por 2 anos
                </li>
                <li className="flex items-center gap-3">
                  ✓ Suporte via WhatsApp
                </li>
              </ul>

              <button
                onClick={() => onNavigate("signup")}
                className="w-full py-5 bg-blue-600 text-white rounded-3xl font-black text-lg hover:bg-blue-700 transition-all"
              >
                Assinar 24 Meses
              </button>
            </div>
          </div>
        </div>
      </section>

      <footer className="py-16 sm:py-24 bg-slate-900 text-center relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col items-center">
          <div className="flex flex-col items-center gap-4 mb-10 sm:mb-12">
            <Logo size="md" className="grayscale opacity-50 brightness-200" />
            <span className="font-black text-white text-2xl sm:text-3xl tracking-tighter uppercase">
              UroInsights Academy
            </span>
          </div>

          <div className="flex flex-wrap justify-center gap-6 sm:gap-10 mb-10 sm:mb-12 text-slate-500 font-bold text-xs sm:text-sm uppercase tracking-widest">
            <button
              onClick={() => onNavigate("terms")}
              className="hover:text-white transition-colors"
            >
              Termos
            </button>
            <button
              onClick={() => onNavigate("privacy")}
              className="hover:text-white transition-colors"
            >
              Privacidade
            </button>
            <button
              onClick={() => onNavigate("contact")}
              className="hover:text-white transition-colors"
            >
              Contato
            </button>
          </div>

          <p className="text-slate-500 text-[10px] sm:text-xs font-black uppercase tracking-[0.4em] mb-6">
            &copy; 2025 UroInsights. Todos os direitos reservados.
          </p>

          <div className="mb-12 sm:mb-16">
            <a
              href="https://www.instagram.com/uroinsights?igsh=MXkyY2syMDc1b2dkcA%3D%3D&utm_source=qr"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-white/5 border border-white/10 text-white hover:bg-white hover:text-slate-900 transition-all shadow-lg"
              aria-label="Instagram UroInsights"
              title="Instagram"
            >
              <svg
                viewBox="0 0 24 24"
                className="w-5 h-5"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M7.75 2h8.5A5.75 5.75 0 0 1 22 7.75v8.5A5.75 5.75 0 0 1 16.25 22h-8.5A5.75 5.75 0 0 1 2 16.25v-8.5A5.75 5.75 0 0 1 7.75 2Zm0 1.5A4.25 4.25 0 0 0 3.5 7.75v8.5A4.25 4.25 0 0 0 7.75 20.5h8.5a4.25 4.25 0 0 0 4.25-4.25v-8.5A4.25 4.25 0 0 0 16.25 3.5h-8.5ZM12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10Zm0 1.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7Zm5.25-2.1a1 1 0 1 1 0 2 1 1 0 0 1 0-2Z" />
              </svg>
            </a>
          </div>

          <div className="pt-10 sm:pt-12 border-t border-slate-800 w-full max-w-lg">
            <button
              onClick={() => onNavigate("admin")}
              className="px-6 sm:px-10 py-4 sm:py-5 bg-white/5 border border-white/10 text-white rounded-3xl font-black text-xs sm:text-sm hover:bg-white hover:text-slate-900 transition-all uppercase tracking-[0.3em] shadow-lg"
            >
              Acesso Restrito Admin 🔒
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Sub-componentes
type BenefitCardProps = {
  icon: string;
  title: string;
  description: string;
  bgColor: string;
};

const BenefitCard = ({ icon, title, description, bgColor }: BenefitCardProps) => (
  <div className="p-8 sm:p-10 bg-white border border-slate-100 rounded-[2.5rem] shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-2 group">
    <div
      className={`w-16 h-16 ${bgColor} rounded-2xl flex items-center justify-center text-3xl mb-8 group-hover:scale-110 transition-transform`}
    >
      {icon}
    </div>
    <h3 className="text-lg sm:text-xl font-black text-slate-900 mb-4 tracking-tight uppercase">
      {title}
    </h3>
    <p className="text-slate-500 font-bold leading-relaxed">{description}</p>
  </div>
);

type PrepItemProps = { title: string; desc: string };

const PrepItem = ({ title, desc }: PrepItemProps) => (
  <div className="bg-white/5 border border-white/10 p-6 rounded-3xl hover:bg-white/10 transition-all group">
    <h4 className="font-black text-amber-400 uppercase text-xs tracking-widest mb-1 group-hover:translate-x-1 transition-transform">
      {title}
    </h4>
    <p className="text-sm font-bold text-blue-100">{desc}</p>
  </div>
);

type AnalyticsPointProps = { title: string; desc: string };

const AnalyticsPoint = ({ title, desc }: AnalyticsPointProps) => (
  <div className="flex items-start gap-4">
    <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-[10px] font-black mt-1">
      ✓
    </div>
    <div>
      <h4 className="font-black text-slate-900 uppercase text-sm tracking-wide mb-1">
        {title}
      </h4>
      <p className="text-slate-500 font-bold text-sm">{desc}</p>
    </div>
  </div>
);

type PerformanceBarProps = { label: string; percent: number; color: string };

const PerformanceBar = ({ label, percent, color }: PerformanceBarProps) => (
  <div className="space-y-2">
    <div className="flex justify-between text-[10px] font-black uppercase tracking-widest text-slate-500">
      <span>{label}</span>
      <span>{percent}%</span>
    </div>
    <div className="w-full bg-slate-100 h-3 rounded-full overflow-hidden shadow-inner">
      <div
        className={`${color} h-full transition-all duration-1000 ease-out`}
        style={{ width: `${percent}%` }}
      />
    </div>
  </div>
);

type ReferenceTileProps = {
  icon: string;
  name: string;
  color: string;
  bgColor: string;
};

const ReferenceTile = ({ icon, name, color, bgColor }: ReferenceTileProps) => (
  <div className="p-6 sm:p-8 bg-white border border-slate-200 rounded-3xl shadow-sm hover:shadow-md transition-all flex flex-col items-center gap-4 min-w-[140px] md:min-w-[180px] group">
    <div
      className={`w-16 h-16 ${bgColor} rounded-2xl flex items-center justify-center text-3xl group-hover:scale-110 transition-transform`}
    >
      {icon}
    </div>
    <span className={`text-[10px] font-black uppercase tracking-[0.2em] ${color}`}>
      {name}
    </span>
  </div>
);
